import { toBase, UNIT_DIMENSION } from "./quantity.js";
/** Nothing edible is denser in energy than pure fat, and that is 900 kcal. */
const MAX_KCAL = 900;
/** A macro is a share of 100 g, so it cannot exceed it. */
const MAX_MACRO = 100;
function num(raw, max) {
    /**
     * Reject the empty values BEFORE coercing, because `Number()` maps almost all
     * of them onto a real number: `Number(null)` and `Number("")` are both 0, and
     * `Number(true)` is 1. A model that answered `"protein": null` — the honest
     * "I don't know", and the exact thing the prompt asks for — would otherwise
     * come out as a confident 0 g of protein.
     *
     * That is the partial-panel failure this module exists to prevent, arriving
     * through the one door left open. Caught by a test, not by reading.
     */
    if (typeof raw !== "number" && typeof raw !== "string")
        return null;
    if (typeof raw === "string" && raw.trim() === "")
        return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0 || n > max)
        return null;
    // One decimal is the precision a nutrition panel is printed at; more would be
    // fabricated, and this number came from a model rather than a laboratory.
    return Math.round(n * 10) / 10;
}
/**
 * Coerces a model's nutrition block, or returns null.
 *
 * ALL FOUR NUMBERS OR NOTHING. A partial panel is the worst outcome: the UI
 * would show three real figures and a blank, which reads as "this food has no
 * fat" rather than "we do not know". Same rule as a doubtful date — see
 * AGENTS.md rule 1.
 */
export function coerceNutrition(raw, basisRaw) {
    if (!raw || typeof raw !== "object")
        return null;
    const o = raw;
    const kcal = num(o.kcal, MAX_KCAL);
    const protein = num(o.protein, MAX_MACRO);
    const carbs = num(o.carbs, MAX_MACRO);
    const fat = num(o.fat, MAX_MACRO);
    if (kcal === null || protein === null || carbs === null || fat === null)
        return null;
    // Macros that sum past 100 g per 100 g are impossible, so the block is not a
    // reading of anything — reject it whole rather than showing part of it.
    if (protein + carbs + fat > MAX_MACRO)
        return null;
    const basis = basisRaw === "100ml" ? "100ml" : "100g";
    return { basis, kcal, protein, carbs, fat };
}
/** "per 100 g" / "per 100 ml", for a caption. */
export function basisLabel(b) {
    return b === "100ml" ? "per 100 ml" : "per 100 g";
}
/**
 * The day a pack is measured against.
 *
 * A stated reference, not a claim about the reader. Larder does household
 * nutrition of PURCHASES and explicitly not per-person tracking
 * (docs/product/nutrition.md), so it has no idea who is eating this or what
 * they need — and a "% of YOUR daily intake" would be inventing a person.
 *
 * 2,000 kcal is the figure Indian nutrition labels themselves are footnoted
 * against, so it is the number a shopper has already seen on the back of the
 * pack rather than one this app made up.
 */
export const REFERENCE_KCAL_DAY = 2000;
/**
 * What fraction of a reference day the whole pack is.
 *
 * The one nutrition fact people can act on. "415 kcal per 100 g" is a figure;
 * "this packet is a third of a day" is a decision — and it is the difference
 * between a panel that gets read and one that gets scrolled past.
 *
 * Null whenever the pack total is unknowable, which is the same condition
 * `perPack` refuses on: no size, or a size in a dimension the panel is not
 * stated in.
 */
export function shareOfReferenceDay(n, q) {
    const whole = perPack(n, q);
    if (!whole)
        return null;
    return whole.kcal / REFERENCE_KCAL_DAY;
}
/** Energy per gram, by macro. The arithmetic every panel is built on. */
const KCAL_PER_G = { protein: 4, carbs: 4, fat: 9 };
/**
 * Where the energy comes from, as three shares of one bar.
 *
 * By ENERGY, not by weight, and the distinction is the whole point: 10 g of fat
 * and 10 g of carbs are the same bar-width by mass and more than twice apart in
 * calories. A bar drawn by weight would be a picture of the wrong quantity.
 *
 * Null when the macros account for no energy at all — black coffee, or a panel
 * whose numbers are all zero. Drawing an empty bar would imply a measurement
 * that was never taken.
 */
export function macroEnergySplit(n) {
    const p = n.protein * KCAL_PER_G.protein;
    const c = n.carbs * KCAL_PER_G.carbs;
    const f = n.fat * KCAL_PER_G.fat;
    const total = p + c + f;
    if (!Number.isFinite(total) || total <= 0)
        return null;
    return { protein: p / total, carbs: c / total, fat: f / total };
}
/**
 * What the whole pack adds up to.
 *
 * Only when the quantity is measured in the same dimension the panel is stated
 * in: you can scale "per 100 ml" by 1 L, but not by "2 packs", because nothing
 * here knows how big a pack is. Returns null rather than guessing — a fabricated
 * pack size would make every downstream total quietly wrong.
 */
export function perPack(n, q) {
    if (!q)
        return null;
    const want = n.basis === "100ml" ? "volume" : "mass";
    if (UNIT_DIMENSION[q.unit] !== want)
        return null;
    // toBase gives ml or g; the panel is per 100 of them.
    const factor = toBase(q) / 100;
    if (!Number.isFinite(factor) || factor <= 0)
        return null;
    const round = (v) => Math.round(v * factor);
    return {
        basis: n.basis,
        kcal: round(n.kcal),
        protein: round(n.protein),
        carbs: round(n.carbs),
        fat: round(n.fat),
    };
}
//# sourceMappingURL=nutrition.js.map